import { Brain } from "lucide-react";
import { ChatBubble } from "./ui";

// Batch 139 — the marketing page's coaching cards, mirroring the real ones.
//
// WHAT THEY MIRROR, and why the fidelity matters. components/NextBestMoveCard.jsx
// and the Best Move block in components/ResultsScreen.jsx are what a signed-up
// user actually sees. If the page invents a prettier format, the first real
// screen is a small disappointment at the exact moment trust is being built.
// So: gold-bordered card, uppercase NEXT BEST MOVE with the Brain icon, a large
// headline, then the reasoning in GOLD ITALIC.
//
// THE ITALIC SLOT IS DELIBERATE. In the real card the italic line is
// nbm-insight, and ResultsScreen feeds it from `reasoning` rather than from
// `why` (its comment at the mount explains that `why` is template-selected and
// largely restates the headline, while `reasoning` is the only per-turn
// specific sentence). So the reasoning belongs in italic, not the muted slot.
//
// THE COLOURS ARE HEX, not the app's tailwind tokens, because every other file
// in marketing/ is. They are the same values: #C9A84C gold, #1A1A1F card,
// #2A2A35 border, #F0F0F0 text.

const Label = ({ children, tone = "gold" }) => (
  <span
    className={`text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 ${
      tone === "red" ? "text-red-400" : "text-[#C9A84C]"
    }`}
  >
    {children}
  </span>
);

// The NEXT BEST MOVE half. `reasoning` is optional: the hero renders a trimmed
// card without it, because 64% of mobile visitors never leave the first
// viewport and average seven seconds there.
export const NextBestMove = ({ headline, reasoning, testId }) => (
  <div
    data-testid={testId || "card-next-best-move"}
    className="rounded-xl border border-[#C9A84C]/50 bg-[#1A1A1F] p-4"
  >
    <Label><Brain size={14} className="flex-shrink-0" /> Next Best Move</Label>
    <p data-testid="card-headline" className="mt-2 text-[#F0F0F0] text-lg font-semibold leading-snug">
      {headline}
    </p>
    {reasoning && (
      <p data-testid="card-reasoning" className="mt-2 text-[#C9A84C]/90 text-sm leading-snug italic">
        {reasoning}
      </p>
    )}
  </div>
);

// The BEST MOVE half. The reply sits in a tinted inner box, as it does in
// ResultsScreen. The real card carries a Copy and Send button; this one does
// not, because a button that copies nothing into nothing is a dead control.
export const BestMove = ({ reply, testId }) => (
  <div
    data-testid={testId || "card-best-move"}
    className="mt-3 rounded-xl border border-[#C9A84C]/60 bg-[#1A1A1F] p-4"
  >
    <Label>Best Move</Label>
    <div className="mt-3 rounded-lg border border-[#C9A84C]/20 bg-[#C9A84C]/[0.08] p-4">
      <p data-testid="card-reply" className="text-[#F0F0F0] text-lg leading-relaxed">
        {reply}
      </p>
    </div>
  </div>
);

// THE STOP CARD IS A DIFFERENT STATE, not a variant of the two above.
//
// server_bridge.py ships the stop payload with "replies": None, and
// ResultsScreen gates BOTH the Next Best Move card and the Best Move card on
// !stop. Its own comment: "Not coaching: there is no Best Move here, no option
// grid, and deliberately no Copy and Send anywhere on screen."
//
// So this renders a directive and a signal and nothing else. No reply, no copy
// control, and deliberately not the app's override link either, which on a page
// with no conversation behind it would be a control that does nothing.
export function StopCard({ directive, signal }) {
  return (
    <div
      data-testid="card-stop"
      className="rounded-xl border-2 border-red-500/70 bg-[#1A1A1F] p-4 space-y-3"
    >
      <Label tone="red">Stop</Label>
      <p data-testid="card-directive" className="text-[#F0F0F0] text-lg font-semibold leading-snug">
        {directive}
      </p>
      <p data-testid="card-signal" className="text-[#A8A8B8] text-sm leading-relaxed">
        {signal}
      </p>
    </div>
  );
}

// The incoming messages above each card. This is the situation, not a claimed
// result: nothing here says what happened next, and no reply of hers is
// invented after ours.
const Incoming = ({ messages }) => (
  <div className="mb-4 space-y-2">
    {messages.map((m, i) => (
      <ChatBubble key={i} from="them" text={m} />
    ))}
  </div>
);

export const CoachingCard = ({ card }) => (
  <div data-testid={`coaching-card-${card.id}`} className="h-full">
    <Incoming messages={card.incoming} />
    {card.kind === "stop" ? (
      <StopCard directive={card.directive} signal={card.signal} />
    ) : (
      <>
        <NextBestMove headline={card.headline} reasoning={card.reasoning} />
        <BestMove reply={card.reply} />
      </>
    )}
  </div>
);
